import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Waves, Baby } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { tr } from "@/i18n/translations";
import { euro, providerName, PROVIDER_META, bookingImg } from "@/lib/format";
import { BrandTag } from "@/components/BrandTag";
import { FlashTimer } from "@/components/FlashTimer";
import { FavButton } from "@/components/FavButton";

export const PropertyCard = ({ p, index = 0 }) => {
  const { t, lang } = useApp();
  const location = useLocation();
  const providers = ["nuitee", "holidu", "booking"].filter((k) => p.prices?.[k] != null);
  const isFlash = p.flash_deal?.active;

  // Carry the active search context (dates/guests/children) into the detail page
  // so the card price and the property page always agree.
  const current = new URLSearchParams(location.search);
  const forward = new URLSearchParams();
  ["check_in", "check_out", "guests", "children_ages"].forEach((k) => {
    const v = current.get(k);
    if (v) forward.set(k, v);
  });
  const qs = forward.toString();
  const stayHref = qs ? `/stay/${p.slug}?${qs}` : `/stay/${p.slug}`;

  // Family signals for the card
  const familyFriendly = (p.amenities || []).includes("family_friendly");
  const searchChildren = (current.get("children_ages") || "").split(",").filter((a) => a !== "").length;
  const searchAdults = current.get("guests") || 2;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid="property-card"
      className="hover-lift group flex flex-col overflow-hidden rounded-2xl border border-sand-border bg-sand-card"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <Link to={stayHref} data-testid="property-card-image-link">
          <img
            src={bookingImg(p.images?.[0], "max500")}
            alt={p.name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </Link>
        <div className="absolute left-3 top-3 flex flex-col gap-2">
          <span className="rounded-none bg-petrol/85 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-wide text-white backdrop-blur">
            {p.zone_name}
          </span>
          {p.guest_favorite && (
            <span data-testid="guest-favourite-badge" className="w-max rounded-none bg-gold px-3 py-1 text-[0.68rem] font-bold uppercase tracking-wide text-petrol-dark">
              ★ {t("card.guest_choice")} · {p.rating}
            </span>
          )}
        </div>
        {isFlash && (
          <div className="absolute right-3 top-3 rounded-none bg-terracotta px-3 py-1.5 text-white shadow-lg">
            <FlashTimer endsAt={p.flash_deal.ends_at} label={t("card.ends_in")} />
          </div>
        )}
        <FavButton slug={p.slug} className={`absolute ${isFlash ? "right-3 top-14" : "right-3 top-3"} shadow-md`} />
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-none bg-white/90 px-2.5 py-1 backdrop-blur">
          <span className="text-sm font-bold text-petrol">{p.rating}</span>
          <Star size={12} className="fill-gold text-gold" />
          <span className="text-xs text-petrol/60">
            {p.reviews_count} {t("card.reviews")}
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-1 flex items-center gap-1">
          {Array.from({ length: p.stars }).map((_, i) => (
            <Star key={i} size={11} className="fill-terracotta text-terracotta" />
          ))}
        </div>
        <Link to={stayHref}>
          <h3 className="font-serif text-xl font-semibold leading-tight text-petrol transition-colors hover:text-terracotta">{p.name}</h3>
        </Link>
        {p.distance_beach != null && (
          <div className="mt-1 flex items-center gap-1.5 text-xs text-petrol/60">
            <Waves size={12} /> {p.distance_beach}m {t("card.to_beach")}
          </div>
        )}

        <p className="mt-3 line-clamp-2 text-sm text-petrol/70">{tr(p.desc, lang)}</p>

        {/* provider mini comparison */}
        <div className="mt-4 flex flex-wrap gap-2">
          {isFlash && (
            <span
              data-testid="provider-price-flash"
              className="flex items-center gap-1.5 rounded-lg border border-transparent bg-terracotta/10 px-2 py-1 text-xs font-bold text-terracotta"
            >
              <span className="h-2 w-2 rounded-full bg-terracotta" />
              {lang === "it" ? "Offerta lampo" : lang === "hr" ? "Brza ponuda" : "Flash deal"}
              <span className="font-mono">{euro(p.flash_deal.deal_price)}</span>
            </span>
          )}
          {providers.map((k) => {
            const isBest = k === p.best_provider && !isFlash;
            return (
              <span
                key={k}
                data-testid={`provider-price-${k}`}
                className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-medium ${
                  isBest ? "border-transparent bg-emerald-50 text-emerald-700" : "border-sand-border text-petrol/70"
                }`}
              >
                {k === "nuitee" ? (
                  <BrandTag compact />
                ) : (
                  <>
                    <span className="h-2 w-2 rounded-full" style={{ background: PROVIDER_META[k].color }} />
                    {providerName(k)}
                  </>
                )}
                {k === "nuitee" && p.live_pricing && (
                  <span data-testid="live-price-badge" className="flex items-center gap-1 rounded bg-emerald-100 px-1 text-[0.6rem] font-bold uppercase text-emerald-700">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                    {t("card.live")}
                  </span>
                )}
                {k === "booking" && p.booking_live && (
                  <span data-testid="live-price-badge" className="flex items-center gap-1 rounded bg-emerald-100 px-1 text-[0.6rem] font-bold uppercase text-emerald-700">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                    {t("card.live")}
                  </span>
                )}
                <span className={`font-mono ${isBest ? "font-bold" : ""}`}>{euro(p.prices[k])}</span>
              </span>
            );
          })}
        </div>

        <div className="mt-auto flex items-end justify-between pt-5">
          <div>
            {(familyFriendly || searchChildren > 0) && (
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {familyFriendly && (
                  <span data-testid="family-welcome-badge" className="inline-flex items-center gap-1 bg-terracotta/10 px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-wide text-terracotta">
                    <Baby size={12} /> {t("card.family_welcome")}
                  </span>
                )}
                {searchChildren > 0 && (
                  <span data-testid="occupancy-badge" className="inline-flex items-center gap-1 bg-petrol/5 px-2 py-1 text-[0.65rem] font-semibold text-petrol/70">
                    {searchAdults} {Number(searchAdults) === 1 ? t("search.guest_one") : t("search.adults").toLowerCase()} · {searchChildren} {searchChildren === 1 ? t("search.child_one") : t("search.children_many")}
                  </span>
                )}
              </div>
            )}
            <span className="overline text-emerald-600" style={{ fontSize: "0.6rem" }}>
              {t("card.best_price")}{p.best_provider ? ` · ${p.best_provider === "nuitee" ? "bestprice" : providerName(p.best_provider)}` : ""}
            </span>
            <div className="flex items-baseline gap-1">
              <span data-testid="property-lowest-price-badge" className="font-serif text-3xl font-semibold text-petrol">
                {euro(p.best_price)}
              </span>
              <span className="text-xs text-petrol/50">{t("card.per_night")}</span>
            </div>
            {p.savings > 0 && (
              <span className="text-xs font-semibold text-terracotta">
                {t("card.save")} {euro(p.savings)}
              </span>
            )}
            {p.nights ? (
              <div data-testid="property-total-price" className="text-xs text-petrol/50">
                {euro(p.total)} {t("search.total_for")} {p.nights} {t("search.nights")}
              </div>
            ) : null}
          </div>
          <Link
            to={stayHref}
            data-testid="property-compare-button"
            className="rounded-none bg-petrol px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terracotta"
          >
            {t("card.compare")}
          </Link>
        </div>
      </div>
    </motion.div>
  );
};
